import API from "Classes/Api";
import {
  ApiPostCallParams,
  ApiGetCallParams,
  ApiDeleteCallParams,
  ApiPutCallParams,
} from "Classes/ApiCallParams";
import { NotificationChannelActivityWindowPayload } from "services/ServicesTypes/Monitoring";

class NotificationService extends API {
  async getNotificationChannels({ clientId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async getTicketingSystems({ clientId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/ticketing-systems`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async getEmailNotifications({ clientId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/email`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async getSmsNotifications({ clientId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/sms`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async getTicketNotifications({ clientId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/ticketing-system`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async getEmailNotification({ clientId, notificationChannelId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/email/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async getSmsNotification({ clientId, notificationChannelId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/sms/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async getTicketNotification({ clientId, notificationChannelId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/ticketing-system/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async createEmailNotification({ clientId, payload }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/email`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
      })
    );
    return result;
  }

  async createSmsNotification({ clientId, payload }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/sms`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
      })
    );
    return result;
  }

  async createTicketNotification({ clientId, payload }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/ticketing-system`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
      })
    );
    return result;
  }

  async updateEmailNotification({ clientId, notificationChannelId, payload }) {
    const result = await super.call(
      new ApiPutCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/email/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
      })
    );
    return result;
  }

  async updateSmsNotification({ clientId, notificationChannelId, payload }) {
    const result = await super.call(
      new ApiPutCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/sms/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
      })
    );
    return result;
  }

  async updateTicketNotification({ clientId, notificationChannelId, payload }) {
    const result = await super.call(
      new ApiPutCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/ticketing-system/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
      })
    );
    return result;
  }

  async deleteEmailNotification({ clientId, notificationChannelId }) {
    const result = await super.call(
      new ApiDeleteCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/email/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async deleteSmsNotification({ clientId, notificationChannelId }) {
    const result = await super.call(
      new ApiDeleteCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/sms/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async deleteTicketNotification({ clientId, notificationChannelId }) {
    const result = await super.call(
      new ApiDeleteCallParams({
        action: `/v2/clients/${clientId}/alerting/notification-channels/ticketing-system/${notificationChannelId}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async getNotificationChannelActivityWindows({ clientId }) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerting/monitoring-activity-windows`,
        headers: { ...API.getAuthHeaders() },
      })
    );
    return result;
  }

  async createNotificationChannelActivityWindow({
    clientId,
    payload,
  }: {
    clientId: string;
    payload: NotificationChannelActivityWindowPayload;
  }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/alerting/monitoring-activity-windows`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
        successMessage: "Template created.",
        failureMessage: "Failed to create template.",
      })
    );
    return result;
  }

  async deleteNotificationChannelActivityWindow({ clientId, monitoringActivityWindowId }) {
    const result = await super.call(
      new ApiDeleteCallParams({
        action: `/v2/clients/${clientId}/alerting/monitoring-activity-windows/${monitoringActivityWindowId}`,
        headers: { ...API.getAuthHeaders() },
        successMessage: "Template deleted.",
        failureMessage: "Failed to delete template.",
      })
    );
    return result;
  }

  async updateNotificationChannelActivityWindow({
    clientId,
    monitoringActivityWindowId,
    payload,
  }: {
    clientId: string;
    monitoringActivityWindowId: number;
    payload: NotificationChannelActivityWindowPayload;
  }) {
    const result = await super.call(
      new ApiPutCallParams({
        action: `/v2/clients/${clientId}/alerting/monitoring-activity-windows/${monitoringActivityWindowId}`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
        successMessage: "Template updated.",
        failureMessage: "Failed to update template.",
      })
    );
    return result;
  }
}

const instance = new NotificationService();
export default instance;
